import React, { useCallback, useEffect, useMemo } from "react";
import { z } from "zod";
import { AnimatePresence, motion } from "framer-motion";
import { useFormContext, Controller } from "react-hook-form";

// utils
import { ACH_KEYWORDS, ADD_PET_MODAL_TRIGGERS, US_STATES } from "@/shared/utils/constants";
import { UIUtils } from "@/shared/utils/UIUtils";
import { PublicConfig } from "@/shared/PublicConfig";
import { cn } from "@/shared/utils";
import Strings from "@/shared/utils/Strings.constants";
import { PtzUsDataUtils } from "../utils/PtzUsDataUtils";

// components
import Terms from "./Terms";
import StripeCheckout from "./StripeCheckout";
import PolicySummary from "@/shared/components/PolicySummary";
import { BillingFrequencyField } from "@/shared/components/BillingFrequency";
import { FormField } from "@/shared/components/FormField";
import { SelectWithLabel } from "@/shared/components/ui/SelectWithLabel";
import { Heading } from "@/shared/components/ui/Heading";
import { InputWithLabel } from "@/shared/components/ui/InputWithLabel";

// hooks
import { useBreeds } from "@/shared/hooks/useBreeds";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useFormParentContext } from "@/shared/contexts/FormParent";
import { useStateFromZipcode } from "@/shared/hooks/useStateFromZipcode";

// types
import { ErrorIdType, PRIORITY_CODE_DEPRECATED } from "@/shared/types/SpotAPI";
import { NonNullableKeys, ObjectPaths } from "@/shared/utils/TypeUtils";
import { FormStepProps, OtherProps } from "@/shared/types/Form";
import { Quote, ExtendedBillingStepSchema, CoverageSettings, InitialPetValues, Policy } from "@/shared/types/Quote.interface";
import PaymentNotice from "@/shared/components/ui/PaymentNotice";
import { useSearchParams } from "next/navigation";
import { FeatureSlot } from "@/shared/components/FeatureSlot";
import { FeatureSlotIds } from "@/shared/components/FeatureSlot/slotIds";
import { UsQuoteFormSteps } from "@/quote-ptz-us/formConfigTypes";
import { useStatsig } from "@/shared/hooks/useStatsig";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";
import PetModal, { PetModalConfig } from "@/shared/components/PetModal";
import { CoverageUtils, PreventiveConfig } from "@/shared/utils/CoverageUtils";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import AddPetBanner from "@/shared/components/MultiPetUpsell/AddPetBanner";
import { getActivePetIndex } from "@/shared/components/MultiPetUpsell/petUtils";
import { SavedPaymentMethod } from "@/add-a-pet/src/components/SavedPaymentMethod";

import LoadingSpinner from "@/shared/components/LoadingSpinner";
import { useMCUser } from "@/shared/hooks/useMCUser";

// preventive care test in billing
export type BillingEditorConfig = {
    petModalConfig?: PetModalConfig;
    getModalInitialValues?: (defaultCoverageSettings?: CoverageSettings, isMultiPetLinked?: boolean) => InitialPetValues;
    hideBillingFrequency?: boolean;
    /** When true, PolicySummary's per-discount rows use `discount.id` as the label instead of discount.name */
    showDiscountId?: boolean;
    /**
     * When true, skips the postal-code → state lookup (useStateFromZipcode / api/utils/postal-code).
     * Used by authenticated flows like add-a-pet where the customer's state is already known and the
     * lookup is redundant network traffic.
     */
    skipStateFromZipLookup?: boolean;
};

// Types for Form and Frequency
export type AllBillingStepProps = z.infer<typeof ExtendedBillingStepSchema>;
type StepKeys = Extract<keyof Quote, keyof AllBillingStepProps>;
type AllBillingStepPaths = ObjectPaths<NonNullableKeys<AllBillingStepProps>>;

interface BillingEditorProps extends FormStepProps<Quote, StepKeys, OtherProps | undefined> {
    editorConfig?: BillingEditorConfig;
    floatingVariant?: "floating" | "floating-inner";
}

export function BillingEditor(props: BillingEditorProps) {
    const { petModalConfig, getModalInitialValues, hideBillingFrequency, showDiscountId, skipStateFromZipLookup } = props?.editorConfig || {};
    const { appState, updateAppState } = useAppLayerContext();
    const {
        asyncErrors,
        showCreditCardFields,
        priorityCode,
        isQuoteUpdating,
        updateQuote,
        stripeMissingBillingInfo,
        petPlansObject,
        underwriter,
        addPetModal,
        activePetIndex = 0,
        isMultiPetLinked,
        showExistingPaymentMethodFlow
    } = appState;
    const quote = props?.value as Quote;
    const isStripeCheckoutEnabled = PublicConfig.PTZ_US.THIRD_PARTY_PAYMENT_PROCESSOR.toUpperCase() === "STRIPE";
    const fieldLabels = PtzUsDataUtils.getPetStepFieldLabels("floating");

    // Fetch MC user data for saved payment method display
    const {
        isLoading: isMcUserLoading,
        savedPaymentMethod,
        isPaperless
    } = useMCUser({
        enabled: !!showExistingPaymentMethodFlow
    });

    const {
        control,
        setValue,
        setError,
        setFocus,
        clearErrors,
        watch,
        formState: { errors, submitCount }
    } = useFormContext<AllBillingStepProps>();

    const { getValues } = useFormParentContext<Quote>();

    const policies = getValues().policies;
    const activePet = policies?.[activePetIndex];

    // Statsig - Add preventive in billing test
    const { showAddPreventiveCare } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showAddPreventiveCare", defaultValue: "" }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });

    // Statsig - Add a pet test
    const { showAddPetUpsell } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showAddPetUpsell", defaultValue: "" }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });
    // Statsig end();

    // Statsig - Edit pet and coverage test
    const { showEditCoveragePet } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showEditCoveragePet", defaultValue: false }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });

    const { getValues: getFormParentValues, setValue: setFormParentValue } = useFormParentContext();

    const handleActivePetChange = (newPetId: string) => {
        const policies = getValues().policies ?? [];

        const index = getActivePetIndex(policies, newPetId);

        if (index === -1) return;

        updateAppState({ activePetIndex: index });
    };

    const billingState = watch("billingInfo.address.state");
    const billingZipcode = watch("billingInfo.address.zipCode");

    // preventive care test billing
    const petPlans = petPlansObject?.petPlans;

    const quotePolicies = quote?.policies;
    const isAnnualBilling = appState.isAnnualBilling;

    const preventiveOptions = useMemo(() => {
        if (!petPlans || !quotePolicies?.length) return {};
        return quotePolicies.reduce<Record<string, ReturnType<typeof CoverageUtils.formatCustomCoverageData>["preventiveCareOptions"]>>((acc, policy) => {
            const plans = petPlans[policy.id ?? ""];
            if (plans?.length) {
                const formatted = CoverageUtils.formatCustomCoverageData(plans, isAnnualBilling);
                acc[policy.id ?? ""] = formatted?.preventiveCareOptions;
            }
            return acc;
        }, {});
    }, [petPlans, quotePolicies, isAnnualBilling]);

    const preventiveConfig: PreventiveConfig = useMemo(
        () => ({
            labels: {
                basic: Strings.PTZ_US.PREVENTIVE_BASIC,
                advanced: Strings.PTZ_US.PREVENTIVE_ADVANCED
            }
        }),
        []
    );

    const onPreventiveChange = useCallback(
        async (value: "wellness-gold" | "wellness-platinum" | "None", petId?: string, applyToAll?: boolean) => {
            const currentValues = getFormParentValues();
            const targetPolicies: Policy[] = currentValues.policies ?? [];

            const updatePetWellness = (policy: Policy) => {
                const currentCoverages = (policy.coverageSettings?.coverages ?? []).filter(c => !c.type?.includes("wellness"));

                if (value !== "None") {
                    const targetPetPlans = petPlansObject?.petPlans?.[policy.id ?? ""] ?? [];
                    const tempSettings: CoverageSettings = {
                        ...policy.coverageSettings!,
                        coverages: [{ type: value }]
                    };
                    const matchingPlan = CoverageUtils.getPlanDataFromCoverageSettings(targetPetPlans, tempSettings, []);
                    if (!matchingPlan) {
                        throw new Error("Unable to resolve wellness plan");
                    }
                    const rider = CoverageUtils.getRiderFromCoverageType(value, matchingPlan.riders);
                    if (!rider?.id) {
                        throw new Error("Unable to resolve wellness rider");
                    }
                    currentCoverages.push({
                        type: value,
                        id: rider.id,
                        preventivePrice: rider.basePrice?.value
                    });
                }

                return {
                    ...policy,
                    coverageSettings: {
                        ...policy.coverageSettings!,
                        coverages: currentCoverages
                    }
                };
            };

            const updatedPolicies = targetPolicies.map((policy, idx) => {
                if (applyToAll) return updatePetWellness(policy);
                if (petId ? policy.id === petId : idx === activePetIndex) return updatePetWellness(policy);
                return policy;
            });

            const updatedQuoteData = { ...currentValues, policies: updatedPolicies };
            const prevLinked = currentValues?.extra?.isMultiPetLinked ?? true;

            try {
                const result = await updateQuote?.mutateAsync({
                    quote: updatedQuoteData,
                    updateType: "UPDATE"
                });

                if (result) {
                    setFormParentValue("policies", result.policies, {
                        shouldDirty: false,
                        shouldTouch: false
                    });

                    // move unlink HERE
                    if (!applyToAll && prevLinked) {
                        updateAppState({ isMultiPetLinked: false });
                        setFormParentValue("extra.isMultiPetLinked", false);
                    }
                }
            } catch {
                updateAppState({ hasUnknownError: true });
            }
        },
        [getFormParentValues, activePetIndex, updateQuote, setFormParentValue, updateAppState, petPlansObject]
    );
    // test end

    /**
     * Start DataDog recording when billing editor has errors
     * This will trigger recording in two cases:
     * 1. Form validation errors (errors object)
     * 2. Async errors (e.g. API responses, postal code validation)
     */
    useEffect(() => {
        const hasErrors = Object.keys(errors).length > 0 || (asyncErrors && asyncErrors.length > 0);
        updateAppState({ isRecordingEnabled: hasErrors });
    }, [errors, asyncErrors, updateAppState]);

    const { breeds } = useBreeds(PublicConfig.PTZ_US.UNDERWRITER);
    const { stateFromZip } = useStateFromZipcode(skipStateFromZipLookup ? undefined : billingZipcode);

    const firstName = props.value?.firstName;
    const lastName = props.value?.lastName;

    const isPetParent = firstName?.toLowerCase() === "pet" && lastName?.toLowerCase() === "parent";

    const needsFirstLastName = !firstName || !lastName;

    const handleExtraChange = async (updateValue: any) => {
        const currentValues = getValues();
        if (isQuoteUpdating) return;
        try {
            const currentExtra = currentValues?.extra;
            const mergedExtra = { ...currentExtra, ...updateValue };
            updateQuote?.mutate({ quote: { ...currentValues, extra: mergedExtra } });
        } catch (error) {
            console.log(error);
        }
    };

    const searchParams = useSearchParams();
    const showAchNotice = ACH_KEYWORDS.some(keyword => searchParams?.toString().toLowerCase().includes(keyword));

    const allBillingErrorsMap: Partial<Record<ErrorIdType, { path?: AllBillingStepPaths; message: string; stripe?: boolean; header?: string; description?: string }>> = useMemo(
        () => ({
            "invalid-billing-address": { path: "billingInfo.address.street1", message: "Invalid billing address" },
            "invalid-billing-city": { path: "billingInfo.address.city", message: Strings.ERRORS.REQUIRED },
            "invalid-billing-name": { path: "billingInfo.nameOnCard", message: "Invalid billing name" },
            "invalid-credit-card-name": { path: "billingInfo.nameOnCard", message: "Invalid billing name" },
            "invalid-phone-number": { path: "phone", message: "Invalid phone number" },
            "invalid-postal-code": { path: "billingInfo.address.zipCode", message: Strings.ERRORS.REQUIRED },
            "invalid-credit-card-cvv": { stripe: true, message: "", header: Strings.ERRORS.CARD_CVV },
            "invalid-credit-card-declined": {
                stripe: true,
                message: "",
                header: appState.isPrepaidCard ? Strings.ERRORS.DECLINE_PREPAID : undefined,
                description: appState.isPrepaidCard ? Strings.ERRORS.DECLINE_PREPAID_DESCRIPTION : undefined
            },
            "invalid-credit-card-number": { stripe: true, message: "" },
            "priority-code-deprecated": { path: undefined, message: "" }
        }),
        [appState.isPrepaidCard]
    );

    useEffect(() => {
        if (!!asyncErrors && asyncErrors?.length > 0 && (submitCount > 0 || stripeMissingBillingInfo)) {
            clearErrors();

            let handledAllErrors = true;
            let asyncStripeError: any | undefined = undefined;

            asyncErrors.forEach(error => {
                const errorMapping = allBillingErrorsMap[error.id];
                if (!!errorMapping && !!errorMapping.path) {
                    setError(errorMapping.path, { message: error?.message ?? errorMapping.message });
                    setFocus(errorMapping.path);
                } else if (!!errorMapping && errorMapping?.stripe) {
                    asyncStripeError = errorMapping;
                } else if (!!errorMapping && error.id === PRIORITY_CODE_DEPRECATED) {
                    // do nothing
                    // pcode error is retried in formController:onSubmit
                } else {
                    handledAllErrors = false;
                }
            });

            // Handle Stripe specific PaymentElement errors
            if (!!asyncStripeError?.stripe) {
                updateAppState({ stripePaymentElementError: asyncStripeError });
            }

            // If we encounter an error that can't be handled, we set a flag to show a generic error message
            if (!handledAllErrors) {
                updateAppState({ hasUnknownError: true });
            }
        }
    }, [allBillingErrorsMap, asyncErrors, clearErrors, setError, setFocus, submitCount, updateAppState, stripeMissingBillingInfo]);

    // Changes State value based on Zip Code value
    useEffect(() => {
        if (!!stateFromZip?.abbreviation && billingState !== stateFromZip.abbreviation) {
            setValue(`billingInfo.address.state`, stateFromZip.abbreviation, { shouldTouch: true, shouldDirty: true });
        }
    }, [setValue, stateFromZip?.abbreviation, billingState]);

    // Clear any payment element errors when the user switches between payment methods
    useEffect(() => {
        if (!showCreditCardFields) {
            updateAppState({ stripePaymentElementError: undefined });
        }
    }, [showCreditCardFields, updateAppState]);

    const floatingVariant: "floating" | "floating-inner" = props.floatingVariant ?? "floating-inner";

    return (
        <div className="flex flex-col gap-7">
            <Heading level="h1" className="sr-only">
                Checkout
            </Heading>
            {/* 2 Col layout */}
            <div className="flex flex-col gap-7 md:flex-row md:gap-8">
                {/* Col 1 on mobile, Col 2 desktop */}
                <div className="order-1 md:order-2 md:w-1/2">
                    <PolicySummary
                        quote={quote}
                        breeds={breeds}
                        config={{ isSingleCol: true, showDiscountId }}
                        showAddPetUpsell={showAddPetUpsell}
                        showEditCoveragePet={showEditCoveragePet}
                        preventiveOptions={preventiveOptions}
                        preventiveConfig={preventiveConfig}
                        underwriter={underwriter as PetUnderwriterType}
                        onPreventiveChange={(value, petId, applyToAll) => onPreventiveChange(value as "wellness-gold" | "wellness-platinum" | "None", petId, applyToAll)}
                        showAddPreventiveCare={showAddPreventiveCare}
                        petModalConfig={petModalConfig}
                        handleActivePetChange={handleActivePetChange}
                    />
                    {/* // statsig test - add a pet banner */}
                    {showAddPetUpsell === "banner" && <AddPetBanner variant="banner" />}
                </div>

                {/* Col 2 on mobile, Col 1 on desktop */}
                <div className="order-2 flex flex-col gap-5 md:order-1 md:w-1/2">
                    {(isPetParent || needsFirstLastName) && (
                        <div className="mx-auto flex w-full flex-col gap-3">
                            <Heading level="h2" className="text-lg font-bold">
                                Pet Parent Info
                            </Heading>
                            <div className={cn("flex flex-col gap-6")}>
                                <Controller
                                    name="billingInfo.firstName"
                                    control={control}
                                    render={({ field: { ref, onChange, ...rest } }) => (
                                        <FormField className="min-w-[250px] flex-1" error={errors?.billingInfo?.firstName?.message} errorId="error-given-name">
                                            <InputWithLabel
                                                variant={floatingVariant}
                                                label={fieldLabels.firstName.label}
                                                inputRef={ref}
                                                error={errors?.billingInfo?.firstName?.message}
                                                inputProps={{
                                                    ...rest,
                                                    autoComplete: "given-name",
                                                    onChange: e => {
                                                        if (errors?.billingInfo?.firstName) {
                                                            clearErrors("billingInfo.firstName");
                                                        }
                                                        onChange(e);
                                                        const lastName = getValues(`billingInfo.lastName`);
                                                        const newNameOnCard = `${e.target.value} ${lastName ?? ""}`;
                                                        setValue("firstName", e.target.value.trim().replace(/\s+/g, " "), { shouldTouch: true, shouldDirty: true });
                                                        setValue("billingInfo.nameOnCard", newNameOnCard.trim().replace(/\s+/g, " "), { shouldTouch: true, shouldDirty: true });
                                                        if (errors?.billingInfo?.nameOnCard) {
                                                            clearErrors("billingInfo.nameOnCard");
                                                        }
                                                    },
                                                    maxLength: 255
                                                }}
                                            />
                                        </FormField>
                                    )}
                                />
                                <Controller
                                    name="billingInfo.lastName"
                                    control={control}
                                    render={({ field: { ref, onChange, ...rest } }) => (
                                        <FormField className="min-w-[250px] flex-1" error={errors?.billingInfo?.lastName?.message} errorId="error-family-name">
                                            <InputWithLabel
                                                variant={floatingVariant}
                                                label={fieldLabels.lastName.label}
                                                inputRef={ref}
                                                error={errors?.billingInfo?.lastName?.message}
                                                inputProps={{
                                                    ...rest,
                                                    autoComplete: "family-name",
                                                    onChange: e => {
                                                        if (errors?.billingInfo?.lastName) {
                                                            clearErrors("billingInfo.lastName");
                                                        }
                                                        onChange(e);
                                                        const firstName = getValues(`billingInfo.firstName`);
                                                        const newNameOnCard = `${firstName ?? ""} ${e.target.value}`;
                                                        setValue("lastName", e.target.value.trim().replace(/\s+/g, " "), { shouldTouch: true, shouldDirty: true });
                                                        setValue("billingInfo.nameOnCard", newNameOnCard.trim().replace(/\s+/g, " "), { shouldTouch: true, shouldDirty: true });
                                                        if (errors?.billingInfo?.nameOnCard) {
                                                            clearErrors("billingInfo.nameOnCard");
                                                        }
                                                    },
                                                    maxLength: 255
                                                }}
                                            />
                                        </FormField>
                                    )}
                                />
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col gap-3">
                        <Heading level="h2" className="text-lg font-bold">
                            {Strings.PAYMENT_INFO}
                        </Heading>
                        <div className="flex grow flex-col">
                            <FeatureSlot formId={appState.formID} stepId={UsQuoteFormSteps.BILLING.id} slotId={FeatureSlotIds.MEMBERSHIP} quote={quote} className="mb-5" />
                            {/* Mounted only when the input actually renders — it owns the ENG-18612 exposure. */}
                            {!hideBillingFrequency && <BillingFrequencyField quote={quote} variant={floatingVariant} />}
                            {!showExistingPaymentMethodFlow && isStripeCheckoutEnabled && <StripeCheckout quote={quote} />}
                        </div>
                    </div>
                    {showAchNotice && <PaymentNotice />}
                    {!showExistingPaymentMethodFlow && (
                        <AnimatePresence mode="sync">
                            {showCreditCardFields && (
                                <motion.div key="outer" initial={{ height: 0 }} animate={{ height: `auto` }} exit={{ height: 0 }}>
                                    <motion.div key="inner" initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.25 } }} exit={{ opacity: 0 }}>
                                        <div className="flex flex-col">
                                            <Heading level="h2" className="mb-3 text-lg font-bold">
                                                Billing Address
                                            </Heading>
                                            <div className={cn("flex flex-col gap-6")}>
                                                <Controller
                                                    name="billingInfo.nameOnCard"
                                                    control={control}
                                                    render={({ field: { ref, ...rest } }) => (
                                                        <FormField className="min-w-[250px]" error={errors?.billingInfo?.nameOnCard?.message} errorId="error-creditCard-nameOnCard">
                                                            <InputWithLabel
                                                                variant={floatingVariant}
                                                                label={Strings.PTZ_US.BILLING_NAME}
                                                                inputRef={ref}
                                                                error={errors?.billingInfo?.nameOnCard?.message}
                                                                inputProps={{
                                                                    ...rest,
                                                                    autoComplete: "cc-name",
                                                                    "aria-describedby": errors?.billingInfo?.nameOnCard ? "error-creditCard-nameOnCard" : undefined,
                                                                    maxLength: 50
                                                                }}
                                                            />
                                                        </FormField>
                                                    )}
                                                />
                                                <Controller
                                                    name="billingInfo.address.street1"
                                                    control={control}
                                                    render={({ field: { ref, onChange, ...rest } }) => (
                                                        <FormField
                                                            className="min-w-[250px] flex-1"
                                                            error={errors?.billingInfo?.address?.street1?.message}
                                                            errorId="error-address-street1"
                                                        >
                                                            <InputWithLabel
                                                                variant={floatingVariant}
                                                                label={Strings.PTZ_US.ADDRESS_LINE_1}
                                                                inputRef={ref}
                                                                error={errors?.billingInfo?.address?.street1?.message}
                                                                inputProps={{
                                                                    ...rest,
                                                                    onChange: event => {
                                                                        onChange(event);
                                                                    },
                                                                    autoComplete: "address-line1",
                                                                    "aria-describedby": errors?.billingInfo?.address?.street1 ? "error-address-street1" : undefined,
                                                                    maxLength: 255
                                                                }}
                                                            />
                                                        </FormField>
                                                    )}
                                                />

                                                <Controller
                                                    name="billingInfo.address.street2"
                                                    control={control}
                                                    render={({ field: { ref, onChange, ...rest } }) => (
                                                        <FormField
                                                            className="min-w-[250px] flex-1"
                                                            error={errors?.billingInfo?.address?.street2?.message}
                                                            errorId="error-address-street2"
                                                        >
                                                            <InputWithLabel
                                                                variant={floatingVariant}
                                                                label={Strings.PTZ_US.ADDRESS_LINE_2}
                                                                inputRef={ref}
                                                                error={errors?.billingInfo?.address?.street2?.message}
                                                                inputProps={{
                                                                    ...rest,
                                                                    onChange: event => {
                                                                        onChange(event);
                                                                    },
                                                                    autoComplete: "address-line2",
                                                                    "aria-describedby": errors?.billingInfo?.address?.street2 ? "error-address-street2" : undefined,
                                                                    maxLength: 255
                                                                }}
                                                            />
                                                        </FormField>
                                                    )}
                                                />
                                                <Controller
                                                    name="billingInfo.address.city"
                                                    control={control}
                                                    render={({ field: { ref, onChange, ...rest } }) => (
                                                        <FormField
                                                            className="min-w-[250px] flex-1"
                                                            error={errors?.billingInfo?.address?.city?.message}
                                                            errorId="error-address-city"
                                                        >
                                                            <InputWithLabel
                                                                variant={floatingVariant}
                                                                label="City"
                                                                inputRef={ref}
                                                                error={errors?.billingInfo?.address?.city?.message}
                                                                inputProps={{
                                                                    ...rest,
                                                                    onChange: event => {
                                                                        onChange(event);
                                                                    },
                                                                    autoComplete: "address-level2",
                                                                    "aria-describedby": errors?.billingInfo?.address?.city ? "error-address-city" : undefined,
                                                                    maxLength: 100
                                                                }}
                                                            />
                                                        </FormField>
                                                    )}
                                                />
                                                <div className="flex gap-5 lg:col-span-2 xl:col-span-1">
                                                    <Controller
                                                        name="billingInfo.address.state"
                                                        control={control}
                                                        render={({ field: { onChange, value, ref } }) => (
                                                            <FormField className="flex-1" error={errors?.billingInfo?.address?.state?.message} errorId="error-address-state">
                                                                <SelectWithLabel
                                                                    ref={ref}
                                                                    id="billing-address-state"
                                                                    labelId="billing-address-state-label"
                                                                    label="State"
                                                                    value={value || ""}
                                                                    options={US_STATES}
                                                                    onValueChange={val => {
                                                                        onChange(val);
                                                                        setValue("billingInfo.address.zipCode", "", { shouldDirty: true });
                                                                    }}
                                                                    placeholder="Select a state"
                                                                    constrainWidthToTrigger={true}
                                                                    variant={floatingVariant}
                                                                    triggerProps={{
                                                                        "aria-describedby": errors?.billingInfo?.address?.state ? "error-address-state" : undefined
                                                                    }}
                                                                />
                                                            </FormField>
                                                        )}
                                                    />

                                                    <Controller
                                                        name="billingInfo.address.zipCode"
                                                        control={control}
                                                        render={({ field: { ref, value, onChange, ...rest } }) => (
                                                            <FormField className="flex-1" error={errors?.billingInfo?.address?.zipCode?.message} errorId="error-address-zipCode">
                                                                <InputWithLabel
                                                                    variant={floatingVariant}
                                                                    label="Zip Code"
                                                                    inputRef={ref}
                                                                    error={errors?.billingInfo?.address?.zipCode?.message}
                                                                    inputProps={{
                                                                        ...rest,
                                                                        placeholder: fieldLabels.zipCode.placeholder,
                                                                        autoComplete: "postal-code",
                                                                        value: UIUtils.maskPostalCodeUS(value),
                                                                        onChange: e => onChange(UIUtils.maskPostalCodeUS(e.target.value)),
                                                                        "aria-describedby": errors?.billingInfo?.address?.zipCode ? "error-address-zipCode" : undefined
                                                                    }}
                                                                />
                                                            </FormField>
                                                        )}
                                                    />
                                                </div>
                                                <Controller
                                                    name="phone"
                                                    control={control}
                                                    render={({ field: { ref, value, onChange, ...rest } }) => (
                                                        <FormField error={errors?.phone?.message} errorId="error-phone">
                                                            <InputWithLabel
                                                                variant={floatingVariant}
                                                                label={Strings.MOBILE_PHONE}
                                                                inputRef={ref}
                                                                error={errors?.phone?.message}
                                                                inputProps={{
                                                                    ...rest,
                                                                    autoComplete: "tel-national",
                                                                    placeholder: fieldLabels.phone.placeholder,
                                                                    type: "tel",
                                                                    value: UIUtils.maskPhone(value),
                                                                    onChange: e => onChange(UIUtils.maskPhone(e.target.value)),
                                                                    "aria-describedby": errors?.phone ? "error-phone" : undefined
                                                                }}
                                                            />
                                                        </FormField>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    )}

                    <Terms
                        wrapperClass={cn("max-w-[580px]")}
                        handleExtraChange={handleExtraChange}
                        quoteExtra={quote.extra}
                        isQuoteUpdating={isQuoteUpdating}
                        priorityCode={priorityCode}
                        hidePaperlessOptin={(isMcUserLoading || isPaperless) && !!showExistingPaymentMethodFlow}
                    />
                    {/* Existing payment method + update payment method flows */}
                    {showExistingPaymentMethodFlow && (
                        <>
                            {isMcUserLoading && (
                                <div className="flex items-center justify-center py-4">
                                    <LoadingSpinner size="md" />
                                </div>
                            )}
                            {!isMcUserLoading && (
                                <SavedPaymentMethod
                                    cardType={savedPaymentMethod?.cardType}
                                    lastFour={savedPaymentMethod?.lastFour}
                                    isQuoteUpdating={isQuoteUpdating ?? false}
                                    onSubmit={appState.formHandleContinue}
                                />
                            )}
                        </>
                    )}
                    {/* Modal needed for: alternateSubmitButton */}
                    {petModalConfig && getModalInitialValues && (
                        <PetModal
                            initialValues={{
                                policies: [getModalInitialValues(activePet?.coverageSettings, !!isMultiPetLinked)]
                            }}
                            isOpen={addPetModal === ADD_PET_MODAL_TRIGGERS.ALT_SUBMIT_CTA}
                            onClose={() => updateAppState({ addPetModal: undefined })}
                            config={petModalConfig}
                            handleActivePetChange={handleActivePetChange}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
